import { ParametersExtractor } from '../parameters-extractor/parameters-extractor';
import { ContainerDimensionsGetter } from '../container-dimension-retriever/container-dimension-retriever';
import { Experience } from '../types/experience';
import { IsProgrammaticRetriever } from '../is-programmatic-retriever/is-programmatic-retriever';

export class ExperienceRetrieverError extends Error {
  constructor(
    message:
      | 'MissingAdUnitTypeOrAdSetIdOrDimensions'
      | 'ExperienceFetchError'
      | 'NetworkError'
      | 'RequestTimeoutError',
    public details?: any
  ) {
    super(message);
    this.name = 'ExperienceRetrieverError';
  }
}

export class ExperienceRetriever {
  constructor(
    protected parametersExtractor: ParametersExtractor,
    protected containerDimensionsRetriever: ContainerDimensionsGetter,
    protected isProgrammaticRetriever: IsProgrammaticRetriever,
    protected routes: {
      experienceRouteUrl: string;
    }
  ) {}

  public async fetchExperience(aWindow: Window): Promise<Experience> {
    const adUnitType = this.parametersExtractor.getHostSlotParameter<string>(
      'adUnitType',
      aWindow
    );
    const inArticleType = this.parametersExtractor.getHostSlotParameter<
      string | boolean
    >('inArticleType', aWindow);
    const adSetId = this.parametersExtractor.getContextParameters<string>(
      'adsSetId',
      aWindow
    );
    const experienceId = this.parametersExtractor.getContextParameters<string>(
      'experienceId',
      aWindow
    );
    const macros =
      this.parametersExtractor.getContextParameters<{ [key: string]: string }>(
        'macros',
        aWindow
      ) || {};
    const dimensions = this.containerDimensionsRetriever.getContainerDimensions(
      aWindow,
      macros
    );
    const isProgrammatic = this.isProgrammaticRetriever.getIsProgrammatic();

    if ((!adUnitType || !dimensions) && !adSetId && !experienceId) {
      throw new ExperienceRetrieverError(
        'MissingAdUnitTypeOrAdSetIdOrDimensions',
        {
          adUnitType,
          adSetId,
          dimensions
        }
      );
    }

    const url = this.getExperienceUrl(
      adSetId,
      experienceId,
      adUnitType === undefined
        ? undefined
        : adUnitType +
            (inArticleType != undefined && (inArticleType as boolean) !== false
              ? `-${inArticleType}`
              : ''),
      dimensions,
      isProgrammatic
    );

    return await this.fetchUrl(url);
  }

  protected fetchUrl(url: string): Promise<Experience> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      xhr.open('GET', url);
      xhr.responseType = 'json';

      xhr.onload = function() {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(xhr.response);
        } else {
          reject(
            new ExperienceRetrieverError('ExperienceFetchError', {
              serverResponseStatus: xhr.status
            })
          );
        }
      };

      xhr.onerror = function() {
        reject(new ExperienceRetrieverError('NetworkError'));
      };

      xhr.ontimeout = function() {
        reject(new ExperienceRetrieverError('RequestTimeoutError'));
      };

      xhr.send();
    });
  }

  protected getExperienceUrl(
    adSetId: string | undefined,
    experienceId: string | undefined,
    adUnitType: string | undefined,
    dimensions: { width: number; height: number } | undefined,
    isProgrammatic: boolean
  ): string {
    const url = new URL(this.routes.experienceRouteUrl);
    const params = new URLSearchParams();
    const entries: Array<[string, string | undefined]> = [
      ['adsSetId', adSetId],
      ['adUnitType', adUnitType],
      ['dimensions', dimensions && `${dimensions.width}x${dimensions.height}`],
      ['experienceId', experienceId],
      ['isProgrammatic', String(isProgrammatic)]
    ];
    entries.forEach(([key, value]) => {
      if (value !== undefined) {
        params.append(key, value);
      }
    });
    url.search = params.toString();
    return url.toString();
  }
}
